import { appCatalogReference } from '@erp/json-renderer/apps';
import {
  REPORT_DATE_PRESETS,
  REPORT_MAX_FILTERS,
  WORKBOOK_REPORT_DATA_MAX_REQUESTS,
  type WorkbookAppOutput,
  type WorkbookDefinition,
} from '@erp/shared-types';
import { WorkbookAgentEditError } from './workbookAgentEdits';

/**
 * How an App Output's App is written: a json-render view over feeds that read
 * this Workbook's Worksheets as the viewer. It runs no tools and has no
 * commands, so filters and selection are view state.
 */
export function workbookAppGuide(): string {
  return `An App Output's App is a manifest: {"schemaVersion": 1, "view": <json-render spec: {"root", "elements", "state"}>, "feeds": {<key>: <feed>}}. It reads this Workbook's Worksheets as the person viewing it, so it has no "commands", no tool feeds, no workbookId and no sop.

FEEDS (at most ${WORKBOOK_REPORT_DATA_MAX_REQUESTS}): each reads one Worksheet's current result.
{"kind": "workbook", "sheetId": "<worksheet id>", "read": {"kind": "rows", "limit": 500}, "filters": []}
or "read": {"kind": "aggregate", "aggregation": {"groupBy": ["Region"], "measures": [{"column": "Sales", "fn": "sum"}], "limit": 10, "order": "desc"}}
A feed's value lands in state at /<key> as {"fields": [column names], "rows": [{column: value}], "meta": {"rowCount", "truncated"}}; its status at /$data/<key> as {"loading", "error"}. Bind a table to {"$state": "/<key>/rows"}.
Filters (at most ${REPORT_MAX_FILTERS} per feed) narrow a feed by view state, read again whenever that state changes:
{"kind": "values", "column": "Region", "values": {"$state": "/region"}}
{"kind": "date-range", "column": "Order Date", "preset": {"$state": "/period"}} (presets: ${REPORT_DATE_PRESETS.join(', ')}; or "from"/"to" as YYYY-MM-DD)
{"kind": "number-range", "column": "Margin", "min": {"$state": "/minMargin"}}
An empty state value leaves that bound off. Put each filter's starting value in view.state.

Use column names exactly as the Worksheet's result has them (read them first). The view uses json-render's built-in state actions (setState, pushState, removeState) only.

${appCatalogReference()}`;
}

/** The App Output an App is built for, or why it can't be. */
export function appOutputFor(
  definition: WorkbookDefinition,
  outputId: string,
): WorkbookAppOutput {
  const output = definition.outputs.find((item) => item.id === outputId);
  if (output?.kind !== 'app')
    throw new WorkbookAgentEditError(
      `Output ${outputId} is not an App Output of this workbook. Add one with edit_workbook first.`,
    );
  return output;
}

/** The Definition with the built App linked to its Output. */
export function withLinkedApp(
  definition: WorkbookDefinition,
  outputId: string,
  appId: string,
): WorkbookDefinition {
  return {
    ...definition,
    outputs: definition.outputs.map((output) =>
      output.id === outputId && output.kind === 'app'
        ? { ...output, appId }
        : output,
    ),
  };
}

/** A unique App slug from its name, e.g. `margin-review-3f9a1c`. */
export function appSlug(name: string, suffix: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return base ? `${base}-${suffix}` : `app-${suffix}`;
}
